import { Injectable, computed, signal } from '@angular/core';

const STORAGE_KEY = 'taskeo.billing.gate';

/**
 * Motivo auxiliar para telemetria/UI (errorKey do body 402 quando disponível).
 */
export interface BillingGateReason {
  /** Código curto (ex.: "Trial_Expired", "Subscription_Past_Due"). */
  code?: string;
  /** Mensagem já localizada vinda do backend. */
  message?: string;
}

interface StoredGate {
  companyId: string;
  reason?: BillingGateReason;
}

/**
 * Estado global de "billing bloqueado" por empresa. Ativado por 402 Payment Required
 * do backend e consumido pelo `paymentRequiredInterceptor`, `billingGateChildGuard`
 * e seletor de empresa. Limpo automaticamente pelo `BillingStore` quando a assinatura
 * volta a `Active`.
 *
 * Persistido em `sessionStorage` para sobreviver a reload (senão o usuário conseguiria
 * "escapar" recarregando antes da próxima request 402 chegar).
 */
@Injectable({ providedIn: 'root' })
export class BillingGateService {
  private readonly blockedCompanyIdSignal = signal<string | null>(null);
  private readonly reasonSignal = signal<BillingGateReason | null>(null);

  readonly blockedCompanyId = this.blockedCompanyIdSignal.asReadonly();
  readonly reason = this.reasonSignal.asReadonly();
  readonly blocked = computed(() => this.blockedCompanyIdSignal() !== null);

  constructor() {
    this.restoreFromStorage();
  }

  /** Ativa o gate para uma empresa. Idempotente para a mesma empresa. */
  block(companyId: string | null, reason?: BillingGateReason | null): void {
    const normalized = companyId?.trim() || null;
    if (!normalized) return;
    this.blockedCompanyIdSignal.set(normalized);
    this.reasonSignal.set(reason ?? null);
    this.persist({ companyId: normalized, reason: reason ?? undefined });
  }

  /** Desativa o gate (chamado quando assinatura volta a Active ou no logout). */
  clear(): void {
    this.blockedCompanyIdSignal.set(null);
    this.reasonSignal.set(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage indisponível — seguir com estado em memória
    }
  }

  /** Determina se uma empresa específica está com o gate ativo. */
  isBlockedFor(companyId: string | null | undefined): boolean {
    const c = companyId?.trim();
    if (!c) return false;
    return this.blockedCompanyIdSignal() === c;
  }

  private persist(data: StoredGate): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // storage indisponível — seguir com estado em memória
    }
  }

  private restoreFromStorage(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<StoredGate> | null;
      const companyId = parsed?.companyId?.trim();
      if (!companyId) return;
      this.blockedCompanyIdSignal.set(companyId);
      this.reasonSignal.set(parsed?.reason ?? null);
    } catch {
      // payload corrompido — ignorar
    }
  }
}
